import type { IsEnableDownloadSales } from './valueObjects/isEnableDownloadSales';
import type { IsEnableNurseryPayment } from './valueObjects/isEnableNurseryPayment';
import type { NurseryId } from './valueObjects/nurseryId';
import type { NurseryPaymentTitle } from './valueObjects/nurseryPaymentTitle';
import type { SctActActor } from './valueObjects/sctActActor';
import type { SctActAddress } from './valueObjects/sctActAddress';
import type { SctActDeliveryTime } from './valueObjects/sctActDeliveryTime';
import type { SctActEmail } from './valueObjects/sctActEmail';
import type { SctActExchange } from './valueObjects/sctActExchange';
import type { SctActExchangeFee } from './valueObjects/sctActExchangeFee';
import type { SctActHowToPay } from './valueObjects/sctActHowToPay';
import type { SctActPaymentTerm } from './valueObjects/sctActPaymentTerm';
import type { SctActPrice } from './valueObjects/sctActPrice';
import type { SctActPriceOther } from './valueObjects/sctActPriceOther';
import type { SctActSupervisor } from './valueObjects/sctActSupervisor';
import type { SctActTel } from './valueObjects/sctActTel';
import type { SctActTelTime } from './valueObjects/sctActTelTime';
import type { SquareApplicationId } from './valueObjects/squareApplicationId';
import type { SquareLocationId } from './valueObjects/squareLocationId';

import { Entity } from '@/domain/entity';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 公開API設定プロパティ
 */
export type PublicApiSettingsProps = {
  /** 施設ID */
  nurseryId: NurseryId,
  /** 施設払い可能か */
  isEnableNurseryPayment: IsEnableNurseryPayment,
  /** ダウンロード販売可能か */
  isEnableDownloadSales: IsEnableDownloadSales | undefined,
  /** 施設払いのタイトル */
  nurseryPaymentTitle: NurseryPaymentTitle,
  /** SquareのApplication ID */
  squareApplicationId: SquareApplicationId,
  /** SquareのLocation ID */
  squareLocationId: SquareLocationId,
  /** 特定商取引法に基づく表記 販売業者 */
  sctActActor: SctActActor,
  /** 特定商取引法に基づく表記 代表責任者 */
  sctActSupervisor: SctActSupervisor,
  /** 特定商取引法に基づく表記 住所 */
  sctActAddress: SctActAddress,
  /** 特定商取引法に基づく表記 電話 */
  sctActTel: SctActTel,
  /** 特定商取引法に基づく表記 電話受付時間 */
  sctActTelTime: SctActTelTime,
  /** 特定商取引法に基づく表記 メール */
  sctActEmail: SctActEmail,
  /** 特定商取引法に基づく表記 価格 */
  sctActPrice: SctActPrice,
  /** 特定商取引法に基づく表記 商品代金以外に必要な料金（その他） */
  sctActPriceOther: SctActPriceOther,
  /** 特定商取引法に基づく表記 支払い方法 */
  sctActHowToPay: SctActHowToPay,
  /** 特定商取引法に基づく表記 支払い時期 */
  sctActPaymentTerm: SctActPaymentTerm,
  /** 特定商取引法に基づく表記 商品の引き渡し時期 */
  sctActDeliveryTime: SctActDeliveryTime,
  /** 特定商取引法に基づく表記 返品・交換 */
  sctActExchange: SctActExchange,
  /** 特定商取引法に基づく表記 返品送料 */
  sctActExchangeFee: SctActExchangeFee,
}

/**
 * 公開API設定JSON
 */
export type PublicApiSettingsJson = {
  /** 施設ID */
  nurseryId: string,
  /** 施設払い可能か */
  isEnableNurseryPayment: boolean,
  /** ダウンロード販売可能か */
  isEnableDownloadSales: boolean | undefined,
  /** 施設払いのタイトル */
  nurseryPaymentTitle: string,
  /** SquareのApplication ID */
  squareApplicationId: string,
  /** SquareのLocation ID */
  squareLocationId: string,
  /** 特定商取引法に基づく表記 販売業者 */
  sctActActor: string,
  /** 特定商取引法に基づく表記 代表責任者 */
  sctActSupervisor: string,
  /** 特定商取引法に基づく表記 住所 */
  sctActAddress: string,
  /** 特定商取引法に基づく表記 電話 */
  sctActTel: string,
  /** 特定商取引法に基づく表記 電話受付時間 */
  sctActTelTime: string,
  /** 特定商取引法に基づく表記 メール */
  sctActEmail: string,
  /** 特定商取引法に基づく表記 価格 */
  sctActPrice: string,
  /** 特定商取引法に基づく表記 商品代金以外に必要な料金（その他） */
  sctActPriceOther: string,
  /** 特定商取引法に基づく表記 支払い方法 */
  sctActHowToPay: string,
  /** 特定商取引法に基づく表記 支払い時期 */
  sctActPaymentTerm: string,
  /** 特定商取引法に基づく表記 商品の引き渡し時期 */
  sctActDeliveryTime: string,
  /** 特定商取引法に基づく表記 返品・交換 */
  sctActExchange: string,
  /** 特定商取引法に基づく表記 返品送料 */
  sctActExchangeFee: string,
}

/**
 * Wagako App公開設定情報Entity
 */
export class PublicApiSettingsEntity extends Entity<
  PublicApiSettingsProps,
  PublicApiSettingsJson
> {
  get deleteType(): DeleteType {
    return DeleteType.NurseryId;
  }

  get deleteKey(): string {
    return this.nurseryId.value;
  }

  toJsonData(): PublicApiSettingsJson {
    return {
      nurseryId: this.nurseryId.value,
      isEnableNurseryPayment: this.isEnableNurseryPayment.value,
      isEnableDownloadSales: this.isEnableDownloadSales?.value,
      nurseryPaymentTitle: this.nurseryPaymentTitle.value,
      squareApplicationId: this.squareApplicationId.value,
      squareLocationId: this.squareLocationId.value,
      sctActActor: this.sctActActor.value,
      sctActSupervisor: this.sctActSupervisor.value,
      sctActAddress: this.sctActAddress.value,
      sctActTel: this.sctActTel.value,
      sctActTelTime: this.sctActTelTime.value,
      sctActEmail: this.sctActEmail.value,
      sctActPrice: this.sctActPrice.value,
      sctActPriceOther: this.sctActPriceOther.value,
      sctActHowToPay: this.sctActHowToPay.value,
      sctActPaymentTerm: this.sctActPaymentTerm.value,
      sctActDeliveryTime: this.sctActDeliveryTime.value,
      sctActExchange: this.sctActExchange.value,
      sctActExchangeFee: this.sctActExchangeFee.value,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    return EntityName.WagakoNurseryPaymentPublicApiSettingsEntity;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.nurseryId.value}`;
  }

  /**
   * 施設ID
   */
  get nurseryId(): NurseryId {
    return this._value.nurseryId;
  }

  /**
   * 施設ID
   */
  set nurseryId(nurseryId: NurseryId) {
    this._value.nurseryId = nurseryId;
  }

  /**
   * 施設払い可能か
   */
  get isEnableNurseryPayment(): IsEnableNurseryPayment {
    return this._value.isEnableNurseryPayment;
  }

  /**
   * 施設払い可能か
   */
  set isEnableNurseryPayment(isEnableNurseryPayment: IsEnableNurseryPayment) {
    this._value.isEnableNurseryPayment = isEnableNurseryPayment;
  }

  /**
   * ダウンロード販売可能か
   */
  get isEnableDownloadSales(): IsEnableDownloadSales | undefined {
    return this._value.isEnableDownloadSales;
  }

  /**
   * 施設払いのタイトル
   */
  get nurseryPaymentTitle(): NurseryPaymentTitle {
    return this._value.nurseryPaymentTitle;
  }

  /**
   * SquareのApplication ID
   */
  get squareApplicationId(): SquareApplicationId {
    return this._value.squareApplicationId;
  }

  /**
   * SquareのApplication ID
   */
  set squareApplicationId(squareApplicationId: SquareApplicationId) {
    this._value.squareApplicationId = squareApplicationId;
  }

  /**
   * SquareのLocation ID
   */
  get squareLocationId(): SquareLocationId {
    return this._value.squareLocationId;
  }

  /**
   * SquareのLocation ID
   */
  set squareLocationId(squareLocationId: SquareLocationId) {
    this._value.squareLocationId = squareLocationId;
  }

  /**
   * 特定商取引法に基づく表記 販売業者
   */
  get sctActActor(): SctActActor {
    return this._value.sctActActor;
  }

  /**
   * 特定商取引法に基づく表記 販売業者
   */
  set sctActActor(sctActActor: SctActActor) {
    this._value.sctActActor = sctActActor;
  }

  /**
   * 特定商取引法に基づく表記 代表責任者
   */
  get sctActSupervisor(): SctActSupervisor {
    return this._value.sctActSupervisor;
  }

  /**
   * 特定商取引法に基づく表記 代表責任者
   */
  set sctActSupervisor(sctActSupervisor: SctActSupervisor) {
    this._value.sctActSupervisor = sctActSupervisor;
  }

  /**
   * 特定商取引法に基づく表記 住所
   */
  get sctActAddress(): SctActAddress {
    return this._value.sctActAddress;
  }

  /**
   * 特定商取引法に基づく表記 住所
   */
  set sctActAddress(sctActAddress: SctActAddress) {
    this._value.sctActAddress = sctActAddress;
  }

  /**
   * 特定商取引法に基づく表記 電話
   */
  get sctActTel(): SctActTel {
    return this._value.sctActTel;
  }

  /**
   * 特定商取引法に基づく表記 電話
   */
  set sctActTel(sctActTel: SctActTel) {
    this._value.sctActTel = sctActTel;
  }

  /**
   * 特定商取引法に基づく表記 電話受付時間
   */
  get sctActTelTime(): SctActTelTime {
    return this._value.sctActTelTime;
  }

  /**
   * 特定商取引法に基づく表記 電話受付時間
   */
  set sctActTelTime(sctActTelTime: SctActTelTime) {
    this._value.sctActTelTime = sctActTelTime;
  }

  /**
   * 特定商取引法に基づく表記 メール
   */
  get sctActEmail(): SctActEmail {
    return this._value.sctActEmail;
  }

  /**
   * 特定商取引法に基づく表記 メール
   */
  set sctActEmail(sctActEmail: SctActEmail) {
    this._value.sctActEmail = sctActEmail;
  }

  /**
   * 特定商取引法に基づく表記 価格
   */
  get sctActPrice(): SctActPrice {
    return this._value.sctActPrice;
  }

  /**
   * 特定商取引法に基づく表記 価格
   */
  set sctActPrice(sctActPrice: SctActPrice) {
    this._value.sctActPrice = sctActPrice;
  }

  /**
   * 特定商取引法に基づく表記 商品代金以外に必要な料金（その他）
   */
  get sctActPriceOther(): SctActPriceOther {
    return this._value.sctActPriceOther;
  }

  /**
   * 特定商取引法に基づく表記 商品代金以外に必要な料金（その他）
   */
  set sctActPriceOther(sctActPriceOther: SctActPriceOther) {
    this._value.sctActPriceOther = sctActPriceOther;
  }

  /**
   * 特定商取引法に基づく表記 支払い方法
   */
  get sctActHowToPay(): SctActHowToPay {
    return this._value.sctActHowToPay;
  }

  /**
   * 特定商取引法に基づく表記 支払い方法
   */
  get sctActPaymentTerm(): SctActPaymentTerm {
    return this._value.sctActPaymentTerm;
  }

  /**
   * 特定商取引法に基づく表記 商品の引き渡し時期
   */
  get sctActDeliveryTime(): SctActDeliveryTime {
    return this._value.sctActDeliveryTime;
  }

  /**
   * 特定商取引法に基づく表記 商品の引き渡し時期
   */
  set sctActDeliveryTime(sctActDeliveryTime: SctActDeliveryTime) {
    this._value.sctActDeliveryTime = sctActDeliveryTime;
  }

  /**
   * 特定商取引法に基づく表記 返品・交換
   */
  get sctActExchange(): SctActExchange {
    return this._value.sctActExchange;
  }

  /**
   * 特定商取引法に基づく表記 返品・交換
   */
  set sctActExchange(sctActExchange: SctActExchange) {
    this._value.sctActExchange = sctActExchange;
  }

  /**
   * 特定商取引法に基づく表記 返品送料
   */
  get sctActExchangeFee(): SctActExchangeFee {
    return this._value.sctActExchangeFee;
  }

  /**
   * 特定商取引法に基づく表記 返品送料
   */
  set sctActExchangeFee(sctActExchangeFee: SctActExchangeFee) {
    this._value.sctActExchangeFee = sctActExchangeFee;
  }
}
